import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import StudentWork from "../../components/StudentWork";
import { toast } from "sonner";
import {
  Loader2, Search, Mail, MapPin, Calendar, User, UserPlus,
  ChevronDown, ChevronUp, Users, ClipboardList, X, Copy, KeyRound, Link2,
  Pencil, Trash2, Undo2, AlertTriangle,
} from "lucide-react";

/**
 * Students and parents, and the only place accounts are made.
 *
 * Until now this screen could only *list* people, and nothing anywhere could
 * create one: the public /signup page writes a lead for the tutor to read and
 * creates no account, and marking that lead "approved" only changes its
 * status. The one route that did create accounts was open to the internet and
 * took a role from the request body - so the way to add a child was a hole
 * that also handed out tutor accounts. It is closed; this is the replacement.
 *
 * **The email address is a username and need not receive mail.** Most children
 * in Years 7-10 have no address of their own, and there is no Google or
 * Microsoft sign-in to fall back on - login is email and password. So the
 * tutor invents a login for the child and puts the family's real address on
 * the parent's account, which is where anything worth sending goes.
 *
 * **The password is shown once.** Only its hash is stored, which is what makes
 * it the child's password rather than a secret the tutor also keeps; a lost one
 * is reset, never looked up. The panel below is therefore the single moment it
 * is readable, and it says so.
 */

const ROLE_TABS = [
  { value: "all",     label: "All",     colour: "#64748b" },
  { value: "student", label: "Students", colour: "#2563eb" },
  { value: "parent",  label: "Parents",  colour: "#7c3aed" },
  // Removed accounts are still here, and reachable. A tab rather than a
  // hidden URL, because an archive nobody can find is a delete with extra
  // steps - and the restore button is the whole reason archiving was chosen.
  { value: "archived", label: "Removed", colour: "#64748b" },
];

const YEARS = [7, 8, 9, 10, 11];

function RoleBadge({ role }) {
  const styles = {
    student: "bg-blue-50 text-blue-700 border-blue-200",
    parent:  "bg-purple-50 text-purple-700 border-purple-200",
    tutor:   "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${styles[role] || ""}`}>
      {role}
    </span>
  );
}

/** Copy that falls back to selecting the text, because clipboard access is
 *  refused often enough that a button which silently does nothing is worse
 *  than no button. */
function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const go = () => {
    navigator.clipboard?.writeText(text)
      .then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })
      .catch(() => toast.info("Select the text and copy it by hand."));
  };
  return (
    <button type="button" onClick={go}
      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600
                 border border-slate-300 rounded-md px-2 py-1 hover:bg-white">
      <Copy size={11} /> {done ? "Copied" : label}
    </button>
  );
}

/**
 * The credentials, at the one moment they exist in readable form.
 *
 * Deliberately explicit that it will not come back: a tutor who closes this
 * having written nothing down has to reset the password, and should know that
 * before closing rather than after.
 */
function Credentials({ name, email, password, mustChange = true, onDone }) {
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-semibold text-amber-900">{name} can now log in</h3>
          <p className="text-xs text-amber-800 mt-0.5">
            Write this down or send it now — the password is not stored anywhere
            readable, so this is the only time it can be shown. If it is lost,
            reset it from their card.
            {mustChange
              ? " They will be asked to choose their own password the first time they log in, so you will not know it after that."
              : " They were not asked to change it, so this stays their password until somebody changes it."}
          </p>
        </div>
        <button onClick={onDone} className="text-amber-700 hover:text-amber-900 shrink-0">
          <X size={18} />
        </button>
      </div>

      <dl className="text-sm space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <dt className="w-24 text-amber-800 shrink-0">Website</dt>
          <dd className="font-mono text-amber-950 select-all">geniuseducation.co.uk/login</dd>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <dt className="w-24 text-amber-800 shrink-0">Email</dt>
          <dd className="font-mono text-amber-950 select-all break-all">{email}</dd>
          <CopyButton text={email} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <dt className="w-24 text-amber-800 shrink-0">Password</dt>
          <dd className="font-mono font-semibold text-amber-950 select-all break-all">{password}</dd>
          <CopyButton text={password} />
        </div>
      </dl>

      <div className="mt-3 pt-3 border-t border-amber-200">
        <CopyButton
          label="Copy all three"
          text={`Website: geniuseducation.co.uk/login\nEmail: ${email}\nPassword: ${password}`}
        />
      </div>
    </div>
  );
}

/**
 * Make a student or a parent.
 *
 * A student's parent can be chosen here only if that parent's account already
 * exists; otherwise it is left blank and linked from the child's card
 * afterwards. Offering to create both at once reads well and gives the tutor
 * two half-made accounts the first time one of the two email addresses is
 * already taken.
 */
function NewAccountModal({ role, parents, onClose, onCreated }) {
  const isStudent = role === "student";
  const [f, setF] = useState({
    name: "", email: "", year: "", tier: "", board: "Edexcel",
    branch: "Wokingham", parent_email: "", must_change_password: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const go = () => {
    if (!f.name.trim()) return toast.error("Give them a name");
    if (!f.email.trim()) return toast.error("Give them a login email address");
    setSaving(true);
    api.post("/admin/users", {
      role,
      name: f.name.trim(),
      email: f.email.trim(),
      branch: f.branch || "Wokingham",
      must_change_password: f.must_change_password,
      ...(isStudent ? {
        year: f.year ? Number(f.year) : null,
        tier: f.tier || null,
        board: f.board || null,
        parent_email: f.parent_email || null,
      } : {}),
    })
      .then((r) => { onCreated(r.data); onClose(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not create the account"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-lg my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">
            {isStudent ? "Add a student" : "Add a parent"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Full name</span>
            <input value={f.name} onChange={(e) => set("name", e.target.value)}
              placeholder={isStudent ? "Aarav Sharma" : "Priya Sharma"}
              className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">
              {isStudent ? "Login email" : "Email address"}
            </span>
            <input value={f.email} onChange={(e) => set("email", e.target.value)}
              placeholder={isStudent ? "aarav.s@geniuseducation.co.uk" : "priya@example.com"}
              className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            <span className="block text-xs text-slate-400 mt-1">
              {isStudent
                ? "This is only their username — it does not have to be a real mailbox. Send the password to the parent."
                : "Use their real address. This is where anything you send them goes."}
            </span>
          </label>

          {isStudent && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1">Year</span>
                  <select value={f.year} onChange={(e) => set("year", e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2">
                    <option value="">—</option>
                    {YEARS.map((y) => <option key={y} value={y}>Year {y}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1">Tier</span>
                  <select value={f.tier} onChange={(e) => set("tier", e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2">
                    <option value="">—</option>
                    <option value="Foundation">Foundation</option>
                    <option value="Higher">Higher</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1">Board</span>
                  <select value={f.board} onChange={(e) => set("board", e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2">
                    <option value="Edexcel">Edexcel</option>
                    <option value="AQA">AQA</option>
                    <option value="OCR">OCR</option>
                  </select>
                </label>
              </div>

              <label className="text-sm block">
                <span className="block text-slate-600 mb-1">Parent (optional)</span>
                <select value={f.parent_email} onChange={(e) => set("parent_email", e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2">
                  <option value="">— none yet —</option>
                  {parents.map((p) => (
                    <option key={p.id} value={p.email}>{p.name} ({p.email})</option>
                  ))}
                </select>
                <span className="block text-xs text-slate-400 mt-1">
                  {parents.length === 0
                    ? "No parent accounts yet. Add the parent first, or link them later from this child's card."
                    : "Only parents who already have an account appear here. You can link one later."}
                </span>
              </label>
            </>
          )}

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Branch</span>
            <input value={f.branch} onChange={(e) => set("branch", e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>

          {/* On by default, and visible rather than implied. A tutor who
              unticks it has decided to hold the password themselves, which is
              occasionally what they want - a demo login shown to a parent - and
              is never what they want by accident. */}
          <label className="flex items-start gap-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
            <input type="checkbox" checked={f.must_change_password}
              onChange={(e) => set("must_change_password", e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-slate-800" />
            <span>
              <span className="font-medium text-slate-700">
                Make them choose their own password at their first login
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Recommended. A password is generated when you save and shown to
                you once — you do not choose it and it is not kept anywhere you
                can read again. With this ticked it works exactly once, to get
                them in, and then they replace it. Untick it only if you need
                the password to keep working, such as a login you are keeping
                for yourself.
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Cancel</button>
          <button onClick={go} disabled={saving}
            className="bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50">
            {saving ? "Creating…" : "Create the account"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Changing someone's details after the account exists.
 *
 * The email is their login, so the field says so and the result says so
 * again - a tutor who changes it and does not tell the child has locked them
 * out without doing anything wrong.
 */
function EditAccountModal({ u, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: u.name || "", email: u.email || "", role: u.role,
    year: u.year || "", tier: u.tier || "", board: u.board || "",
    branch: u.branch || "Wokingham",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isStudent = form.role === "student";

  const save = () => {
    setSaving(true);
    const body = {
      name: form.name, email: form.email, role: form.role,
      branch: form.branch,
      year: isStudent && form.year ? Number(form.year) : null,
      tier: isStudent && form.tier ? form.tier : null,
      board: isStudent && form.board ? form.board : null,
    };
    // A null means "clear it", which is right for a student who is no longer
    // one; for a field simply left blank on a student we send nothing rather
    // than wiping what is there.
    if (isStudent) {
      if (!form.year) delete body.year;
      if (!form.tier) delete body.tier;
      if (!form.board) delete body.board;
    }
    api.patch(`/admin/users/${u.id}`, body)
      .then((r) => {
        toast.success("Saved");
        (r.data.notes || []).forEach((n) => toast(n, { duration: 6000 }));
        onSaved();
        onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save it"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Edit {u.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <label className="block text-xs">
            <span className="text-slate-500">Name</span>
            <input value={form.name} onChange={set("name")}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block text-xs">
            <span className="text-slate-500">Email — this is how they sign in</span>
            <input value={form.email} onChange={set("email")} type="email"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <span className="block text-slate-400 mt-1">
              Change it and you must tell them the new one; their password stays the same.
            </span>
          </label>
          <label className="block text-xs">
            <span className="text-slate-500">Role</span>
            <select value={form.role} onChange={set("role")}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="student">Student</option>
              <option value="parent">Parent</option>
              <option value="tutor">Tutor</option>
            </select>
            {form.role !== u.role && (
              <span className="block text-amber-700 mt-1">
                Changing the role clears the parent link on both sides.
                {form.role === "tutor" && " A tutor can see and change everything."}
              </span>
            )}
          </label>
          {isStudent && (
            <div className="grid grid-cols-3 gap-2">
              <label className="block text-xs">
                <span className="text-slate-500">Year</span>
                <select value={form.year} onChange={set("year")}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  <option value="">—</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <label className="block text-xs">
                <span className="text-slate-500">Tier</span>
                <select value={form.tier} onChange={set("tier")}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  <option value="">—</option>
                  <option value="Foundation">Foundation</option>
                  <option value="Higher">Higher</option>
                </select>
              </label>
              <label className="block text-xs">
                <span className="text-slate-500">Board</span>
                <select value={form.board} onChange={set("board")}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  <option value="">—</option>
                  <option value="Edexcel">Edexcel</option>
                  <option value="AQA">AQA</option>
                  <option value="OCR">OCR</option>
                </select>
              </label>
            </div>
          )}
          <label className="block text-xs">
            <span className="text-slate-500">Branch</span>
            <input value={form.branch} onChange={set("branch")}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const WORK_LABELS = {
  assignments: "assignment", assignment_attempts: "attempt",
  answer_requests: "answer request", groups: "group",
  programme_enrolments: "programme", quiz_attempts: "quiz attempt",
  working: "uploaded photo", coupon_awards: "award",
  chat_messages: "chat message", bookings: "booking",
};

/**
 * Removing someone.
 *
 * What they would take with them is counted and shown *before* anything is
 * offered, because "delete" means two different things depending on the
 * answer, and the tutor cannot know which without being told. Permanent
 * delete appears only for an account with nothing recorded against it - the
 * typo, the test account - and the server refuses it in every other case
 * whatever this screen does.
 */
function RemoveAccountModal({ u, onClose, onDone }) {
  const [work, setWork] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/users/${u.id}/work`)
      .then((r) => setWork(r.data))
      .catch(() => setWork({ counts: {}, total: -1 }));
  }, [u.id]);

  const run = (permanent) => {
    setBusy(true);
    api.delete(`/admin/users/${u.id}`, { params: { permanent } })
      .then((r) => {
        toast.success(r.data.deleted ? `${r.data.name} deleted` : `${r.data.name} removed`);
        onDone();
        onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not remove them"))
      .finally(() => setBusy(false));
  };

  const counts = work?.counts || {};
  const entries = Object.entries(counts);
  const empty = work && work.total === 0;

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Remove {u.name}?</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 text-sm text-slate-700 space-y-3">
          {!work ? (
            <p className="text-slate-400">Checking what they have…</p>
          ) : entries.length > 0 ? (
            <>
              <p>This account has work recorded against it:</p>
              <ul className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 space-y-0.5">
                {entries.map(([k, n]) => (
                  <li key={k}>{n} {WORK_LABELS[k] || k}{n === 1 ? "" : "s"}</li>
                ))}
              </ul>
              <p className="text-xs text-slate-500">
                Removing them stops the login and takes them out of every list, and
                keeps all of the above so your progress figures do not change. You
                can put them back at any time from the <strong>Removed</strong> tab.
              </p>
            </>
          ) : (
            <>
              <p>Nothing has ever been recorded against this account.</p>
              <p className="text-xs text-slate-500">
                So it can be deleted outright, or removed and kept in the
                <strong> Removed</strong> tab like any other.
              </p>
            </>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          {empty && (
            <button onClick={() => run(true)} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
              <AlertTriangle size={13} /> Delete permanently
            </button>
          )}
          <button onClick={() => run(false)} disabled={busy || !work}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {busy ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StudentCard({ u, parents, onExpand, onOpenWork, expanded, onReset, onLink, onEdit, onRemove, onRestore }) {
  const [mustChange, setMustChange] = useState(true);
  const joined = new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const isStudent = u.role === "student";
  const parent = parents.find((p) => p.id === u.linked_parent_id);
  const archived = Boolean(u.archived_at);

  return (
    <div className={`bg-white border rounded-xl p-5 transition-shadow ${
      archived ? "border-slate-200 opacity-70" : "border-slate-200 hover:shadow-sm"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-2">
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-sm shrink-0">
              {u.name?.[0]?.toUpperCase()}
            </div>
            <span className="font-semibold text-slate-900">{u.name}</span>
            <RoleBadge role={u.role} />
            {archived && (
              <span className="text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded-full">
                Removed
              </span>
            )}
            {isStudent && u.year && (
              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Year {u.year}</span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-1.5">
              <Mail size={11} className="text-slate-400 shrink-0" />
              <span className="truncate" title={u.email}>{u.email}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin size={11} className="text-slate-400 shrink-0" />
              {u.branch || "Wokingham"}
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={11} className="text-slate-400 shrink-0" />
              Joined {joined}
            </div>
            {isStudent && u.board && (
              <div className="flex items-center gap-1.5 text-slate-500">
                {u.board} · {u.tier}
              </div>
            )}
          </div>

          {isStudent && (
            <div className="flex items-center gap-1.5 mt-2.5 text-xs text-slate-500">
              <User size={11} className="text-slate-400" />
              {parent
                ? <>Parent: <span className="font-medium text-slate-700">{parent.name}</span></>
                : u.linked_parent_id
                  ? "Linked to a parent account"
                  : <span className="text-slate-400">No parent linked</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0 mt-1">
          {archived ? (
            <button
              onClick={() => onRestore(u)}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
            >
              <Undo2 size={12} /> Put back
            </button>
          ) : (
            <>
              {isStudent && (
                <button
                  onClick={() => onOpenWork(u.id)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
                >
                  <ClipboardList size={12} /> Their work
                </button>
              )}
              <button
                onClick={() => onEdit(u)}
                title="Change their name, login or year group"
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                onClick={() => onRemove(u)}
                title="Remove this account"
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors"
              >
                <Trash2 size={12} />
              </button>
              <button
                onClick={() => onExpand(u.id)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 transition-colors"
              >
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {expanded ? "Less" : "Account"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* The account panel: the two things a tutor needs on a live account and
          could not do at all before - hand a forgotten password back, and
          attach or move a parent. */}
      {expanded && !archived && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {isStudent && (
            <label className="text-xs block">
              <span className="flex items-center gap-1.5 text-slate-500 mb-1">
                <Link2 size={11} /> Parent account
              </span>
              <select
                value={parent?.email || ""}
                onChange={(e) => onLink(u.id, e.target.value || null)}
                className="w-full sm:w-80 border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— none —</option>
                {parents.map((p) => (
                  <option key={p.id} value={p.email}>{p.name} ({p.email})</option>
                ))}
              </select>
              <span className="block text-slate-400 mt-1">
                A parent sees only the children linked to them here.
              </span>
            </label>
          )}

          <div>
            <label className="flex items-start gap-2 text-xs mb-2">
              <input type="checkbox" checked={mustChange}
                onChange={(e) => setMustChange(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 accent-slate-800" />
              <span className="text-slate-600">
                Make them choose their own password at their next login
                <span className="block text-slate-400">
                  Recommended, and it applies to any account however long ago it
                  was made.
                </span>
              </span>
            </label>
            <button
              onClick={() => onReset(u, mustChange)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700
                         border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50"
            >
              <KeyRound size={12} /> Reset their password
            </button>
            <span className="block text-xs text-slate-400 mt-1">
              Makes a new one and shows it once. The old one stops working
              immediately.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminStudents() {
  const [users, setUsers] = useState([]);
  const [parents, setParents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [workFor, setWorkFor] = useState(null);
  const [adding, setAdding] = useState(null);       // "student" | "parent"
  const [credentials, setCredentials] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const fetchUsers = useCallback(async (role, q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // "archived" is a view, not a role: it asks for the removed accounts of
      // both roles rather than narrowing to one.
      if (role === "archived") params.set("archived", "true");
      else if (role !== "all") params.set("role", role);
      if (q) params.set("q", q);
      // Parents are fetched unfiltered as well: the linking dropdowns need
      // every parent, not only the ones matching the current search.
      const [usersRes, statsRes, parentsRes] = await Promise.all([
        api.get(`/admin/students?${params}`),
        api.get("/admin/stats"),
        api.get("/admin/students?role=parent"),
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
      setParents(parentsRes.data.users || []);
    } catch {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(filter, search), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [filter, search, fetchUsers]);

  const handleExpand = (id) => setExpanded((x) => (x === id ? null : id));

  const onCreated = (data) => {
    setCredentials({
      name: data.user.name, email: data.user.email, password: data.password,
      mustChange: data.must_change_password !== false,
    });
    if (data.linked_parent) toast.success(`Linked to ${data.linked_parent}`);
    fetchUsers(filter, search);
  };

  const onReset = (u, mustChange = true) => {
    api.post(`/admin/users/${u.id}/password`, { must_change_password: mustChange })
      .then((r) => setCredentials({
        name: u.name, email: u.email, password: r.data.password,
        mustChange: r.data.must_change_password !== false,
      }))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not reset it"));
  };

  const onLink = (studentId, parentEmail) => {
    api.post(`/admin/users/${studentId}/parent`, { parent_email: parentEmail })
      .then((r) => {
        toast.success(r.data.linked_parent ? `Linked to ${r.data.linked_parent}` : "Parent removed");
        fetchUsers(filter, search);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not link them"));
  };

  const onRestore = (u) => {
    api.post(`/admin/users/${u.id}/restore`)
      .then(() => { toast.success(`${u.name} is back`); fetchUsers(filter, search); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not put them back"));
  };

  const studentCount = users.filter(u => u.role === "student").length;
  const parentCount  = users.filter(u => u.role === "parent").length;

  return (
    <AdminShell title="Students" subtitle="Students and parents, and where their accounts are made" stats={stats}>
      {credentials && (
        <Credentials {...credentials} onDone={() => setCredentials(null)} />
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
        </div>
        <div className="flex gap-2">
          {ROLE_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                filter === t.value ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              style={filter === t.value ? { background: t.colour, borderColor: t.colour } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <p className="text-xs text-slate-400">
          {studentCount} student{studentCount !== 1 ? "s" : ""} · {parentCount} parent{parentCount !== 1 ? "s" : ""}
        </p>
        <div className="flex gap-2">
          <button onClick={() => setAdding("parent")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700
                       border border-slate-300 bg-white rounded-lg px-3 py-2 hover:bg-slate-50">
            <UserPlus size={14} /> Add a parent
          </button>
          <button onClick={() => setAdding("student")}
            className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-3.5 py-2">
            <UserPlus size={14} /> Add a student
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <Users size={36} className="mx-auto text-slate-300 mb-3" />
          <div className="font-medium text-slate-700">
            {filter === "archived" ? "Nobody has been removed" : "No accounts yet"}
          </div>
          <div className="text-sm text-slate-400 mt-1">
            {filter === "archived"
              ? "Accounts you remove appear here, with their work kept, until you put them back."
              : "Add the parent first if you want them linked, then add the child."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <StudentCard
              key={u.id}
              u={u}
              parents={parents}
              onExpand={handleExpand}
              onOpenWork={setWorkFor}
              expanded={expanded === u.id}
              onReset={onReset}
              onLink={onLink}
              onEdit={setEditing}
              onRemove={setRemoving}
              onRestore={onRestore}
            />
          ))}
        </div>
      )}

      {adding && (
        <NewAccountModal role={adding} parents={parents}
          onClose={() => setAdding(null)} onCreated={onCreated} />
      )}
      {workFor && <StudentWork studentId={workFor} onClose={() => setWorkFor(null)} />}
      {editing && (
        <EditAccountModal u={editing} onClose={() => setEditing(null)}
          onSaved={() => fetchUsers(filter, search)} />
      )}
      {removing && (
        <RemoveAccountModal u={removing} onClose={() => setRemoving(null)}
          onDone={() => fetchUsers(filter, search)} />
      )}
    </AdminShell>
  );
}
